import { Badge, Box, Heading, Text, useBreakpointValue } from "@chakra-ui/react-v3";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { Fragment } from "react";
import { useEhrContext } from "../../../shared-logic/ehr-context/ehr-context";
import { HighlightedText } from "../../components/highlighted-text";
import { NoteSnippetsDrawer } from "../../components/note-snippets-drawer";
import { TableFilterPopover } from "../../components/table-filter-popover";
import { useTableFilters } from "../../hooks/use-table-filters";
import { TableRow } from "../../utils/tables-metadata";
import { DiagnosticReportRowData } from "./diagnostic-reports-rows";
import { hasNoteMatches, useNotesFilterOptions } from "./notes-shared";

export type SearchNotesTableProps = {
  rows: TableRow[];
  onRowClick: (row: TableRow) => void;
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  displayName: string;
  onPdfView?: (docId: string) => void;
  isRowViewed?: (row: TableRow) => boolean;
};

/**
 * Notes table component for search mode.
 * Renders card-style layout with expandable note snippets.
 * Parent provides the card wrapper - this component renders content only.
 */
export function SearchNotesTable({
  rows,
  onRowClick,
  filterQuery,
  displayName,
}: SearchNotesTableProps) {
  useEhrContext();
  const filterOptions = useNotesFilterOptions(rows);
  const isSmallScreen = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;

  const { activeFilters, setActiveFilters, filteredRows } = useTableFilters(rows, filterOptions);

  function handleRowClick(row: DiagnosticReportRowData) {
    onRowClick(row);
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={6}>
        <Heading size="md" fontWeight="semibold" color="fg">
          {displayName} ({filteredRows.length})
        </Heading>
        {filterOptions.length > 0 && (
          <TableFilterPopover
            filters={filterOptions}
            activeFilters={activeFilters}
            setActiveFilters={newFilters => setActiveFilters(newFilters)}
          />
        )}
      </Box>

      <Box display="flex" flexDirection="column" gap={3}>
        {filteredRows.map(row => {
          const noteRow = row as unknown as DiagnosticReportRowData;
          const hasMatches = hasNoteMatches(row, filterQuery);

          return (
            <Fragment key={row.id}>
              <Box
                borderRadius="md"
                borderWidth="1px"
                borderColor="border"
                boxShadow="sm"
                overflow="hidden"
                bg="bg.canvas"
              >
                {/* Main row - clickable */}
                <Box
                  cursor="pointer"
                  onClick={() => handleRowClick(noteRow)}
                  _hover={{ bg: "interactive.hover" }}
                  p={4}
                >
                  <Box
                    display="grid"
                    gridTemplateColumns={{ base: "1fr 1fr", md: "repeat(6, 1fr)" }}
                    gap={4}
                  >
                    <Box>
                      {hasMatches ? (
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Text textStyle="table.cell" fontWeight="semibold" color="fg">
                            <HighlightedText
                              text={noteRow.typeOfReport}
                              searchQuery={filterQuery}
                            />
                          </Text>
                          <Badge
                            size="xs"
                            p={0}
                            bg="transparent"
                            display="inline-flex"
                            alignItems="center"
                            gap={1}
                            w="fit-content"
                            fontSize="2xs"
                            opacity={0.7}
                          >
                            Note excerpts below
                          </Badge>
                        </Box>
                      ) : (
                        <Text textStyle="table.cell" color="fg">
                          <HighlightedText text={noteRow.typeOfReport} searchQuery={filterQuery} />
                        </Text>
                      )}
                    </Box>
                    {!isSmallScreen && (
                      <>
                        <Box>
                          <Text textStyle="table.cell" color="fg">
                            <HighlightedText
                              text={noteRow.noteCategory}
                              searchQuery={filterQuery}
                            />
                          </Text>
                        </Box>
                        <Box>
                          <Text textStyle="table.cell" color="fg">
                            <HighlightedText text={noteRow.specialty} searchQuery={filterQuery} />
                          </Text>
                        </Box>
                        <Box>
                          <Text textStyle="table.cell" color="fg">
                            <HighlightedText
                              text={noteRow.reasonForVisit}
                              searchQuery={filterQuery}
                            />
                          </Text>
                        </Box>
                        <Box>
                          <Text textStyle="table.cell" color="fg">
                            <HighlightedText text={noteRow.location} searchQuery={filterQuery} />
                          </Text>
                        </Box>
                      </>
                    )}
                    <Box>
                      <Text textStyle="table.cell" color="fg">
                        <HighlightedText text={noteRow.date} searchQuery={filterQuery} />
                      </Text>
                    </Box>
                  </Box>
                </Box>

                {/* Note snippets drawer */}
                {hasMatches && (
                  <NoteSnippetsDrawer
                    noteData={row.noteData as { fullText: string } | undefined}
                    filterQuery={filterQuery}
                    rowId={row.id}
                    onSnippetClick={() => handleRowClick(noteRow)}
                  />
                )}
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </>
  );
}
